import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { useCampaignEngagement } from '../hooks/useCampaignEngagement';
import { serviceBrand } from '../outreachServiceBrand';
import { resultsMessages as messages } from './resultsMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import card from '../live/Campaign.module.scss';
import styles from './Results.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * "Pitch page clicks" (design outreach-v2 n1): the services that people clicked through to from
 * the pitch pages (`campaigns.engagement`), the most clicked first. Distinct people, not raw clicks.
 */
const LinkClicksComponent: React.FC<{ accountId: number; campaignUuid: string }> = ({ accountId, campaignUuid }) => {
  const intl = useIntl();
  const { engagement } = useCampaignEngagement(accountId, campaignUuid);
  const clicks = useMemo(() => engagement?.clicksByService ?? [], [engagement]);
  const total = useMemo(() => clicks.reduce((sum, row) => sum + row.count, 0), [clicks]);
  const rows = useMemo(() => {
    const top = Math.max(1, ...clicks.map((row) => row.count));
    return clicks.map((row) => (
      <div key={row.service} className={styles.barRow} data-testid="link-clicks-row">
        <span>{serviceBrand(row.service).name}</span>
        <div className={styles.bar} aria-hidden>
          <i style={{ width: `${Math.max(2, Math.round((row.count / top) * 100))}%` }} />
        </div>
        <span className={styles.barValue}>{intl.formatNumber(row.count)}</span>
      </div>
    ));
  }, [clicks, intl]);
  if (engagement === undefined) {
    return null;
  }
  return (
    <section className={card.card} data-testid="link-clicks">
      <div className={card.cardHead}>
        <h3 className={card.cardTitle}>{intl.formatMessage(messages.clicksTitle)}</h3>
        <span className={card.meta}>{intl.formatMessage(messages.clicksMeta, { count: total })}</span>
      </div>
      {rows.length === 0 ? (
        <p className={`${shared.small} m-0`}>{intl.formatMessage(messages.clicksNone)}</p>
      ) : (
        <div className={styles.barRows}>{rows}</div>
      )}
    </section>
  );
};

export const LinkClicks = TypedMemo(LinkClicksComponent);
